import api from '../api';
import locations from './shipments/locations';

const defaultSearchTerm = '';

const getDefaultState = () => ({
  isLoadingShipments: false,
  isLoadingShipment: false,
  shipments: [],
  nextToken: null,
  searchTerm: '',
  errorMessage: '',
  getShipmentsCallCount: 0, // to ensure we only use the latest query results
});

function extractOriginOrDestinationDisplayName(originOrDestination) {
  if (!originOrDestination) {
    return '';
  }

  if (originOrDestination.name) {
    return originOrDestination.name;
  }

  if (originOrDestination.where) {
    if (originOrDestination.where.facility && originOrDestination.where.facility.name) {
      return originOrDestination.where.facility.name;
    }

    if (originOrDestination.where.address) {
      if (originOrDestination.where.address.city) {
        return originOrDestination.where.address.city;
      }
      if (originOrDestination.where.address.country) {
        return originOrDestination.where.address.country;
      }
    }
  }

  return '';
}

function transformShipment(shipment) {
  return {
    ...shipment,
    // Transform the shipment object if needed
    // e.g., format dates, etc.
    pieceCount: (shipment.pieces || {}).total || 0,
    originName: extractOriginOrDestinationDisplayName(shipment.origin),
    originFacilityId: shipment.origin?.where?.facility?.id || null,
    destinationName: extractOriginOrDestinationDisplayName(shipment.destination),
    destinationFacilityId: shipment.destination?.where?.facility?.id || null,
  };
}

export default {
  namespaced: true,
  state: getDefaultState(),
  mutations: {
    RESET_STATE(state) {
      Object.assign(state, getDefaultState());
    },
    ADD_SHIPMENT(state, shipment) {
      if (!state.shipments.find(({ id }) => shipment.id === id)) {
        state.shipments.push(transformShipment(shipment));
      } else {
        // retain the original order
        state.shipments = state.shipments
          .map((d) => (d.id === shipment.id
            ? transformShipment(shipment) : d));
      }
    },
    REMOVE_SHIPMENT(state, removeShipmentId) {
      state.shipments = [...state.shipments.filter(({ id }) => id !== removeShipmentId)];
    },
    UPDATE_SHIPMENT(state, editedShipment) {
      state.shipments = state.shipments
        .map((shipment) => {
          if (shipment.id === editedShipment.id) {
            return transformShipment(editedShipment);
          }
          return shipment;
        });
    },
    SET_SHIPMENTS(state, shipments) {
      state.shipments = [
        ...state.shipments
          .filter((shipmentInState) => !shipments
            .find((newShipment) => shipmentInState.id === newShipment.id)),
        ...shipments.map((shipment) => transformShipment(shipment)),
      ];
    },
    RESET_SHIPMENTS(state) {
      state.shipments = [];
    },
    SET_NEXT_TOKEN(state, nextToken) {
      state.nextToken = nextToken;
    },
    RESET_NEXT_TOKEN(state) {
      state.nextToken = null;
    },
    SET_SEARCH_TERM(state, searchTerm) {
      if (!searchTerm || searchTerm === '') {
        state.searchTerm = defaultSearchTerm;
      } else {
        state.searchTerm = searchTerm;
      }
    },
    RESET_SEARCH_TERM(state) {
      state.searchTerm = defaultSearchTerm;
    },
    START_LOADING_SHIPMENT(state) { state.isLoadingShipment = true; },
    STOP_LOADING_SHIPMENT(state) { state.isLoadingShipment = false; },
    START_LOADING_SHIPMENTS(state) { state.isLoadingShipments = true; },
    STOP_LOADING_SHIPMENTS(state) { state.isLoadingShipments = false; },
    SET_ERROR_MESSAGE(state, errorMessage) {
      state.errorMessage = errorMessage
        .replaceAll('Consignment', 'Shipment') // uppercase 'Consignment' to 'Shipment'
        .replaceAll('consignment', 'shipment'); // lowercase 'consignment' to 'shipment'
    },
    CLEAR_ERROR_MESSAGE(state) { state.errorMessage = ''; },
    INCREMENT_GET_SHIPMENTS_CALL_COUNT(state) { state.getShipmentsCallCount += 1; },
  },
  actions: {
    async refreshGetShipments(context, data = {}) {
      context.commit('RESET_SHIPMENTS');
      context.commit('RESET_NEXT_TOKEN');
      await context.dispatch('getShipments', data);
    },
    async getShipment(context, id) {
      context.commit('CLEAR_ERROR_MESSAGE');
      context.commit('START_LOADING_SHIPMENT');
      try {
        const shipment = await api.getConsignment(id);
        context.commit('ADD_SHIPMENT', shipment);
        return shipment;
      } catch (error) {
        context.commit('SET_ERROR_MESSAGE', error.message);
        return null;
      } finally {
        context.commit('STOP_LOADING_SHIPMENT');
      }
    },
    async getShipments(context) {
      context.commit('INCREMENT_GET_SHIPMENTS_CALL_COUNT');
      const getShipmentQueryId = context.state.getShipmentsCallCount;
      context.commit('CLEAR_ERROR_MESSAGE');
      context.commit('START_LOADING_SHIPMENTS');
      try {
        const shipmentsResponse = await api.getConsignments(
          context.rootState.teams.selectedTeam.id,
          context.state.nextToken,
          context.state.searchTerm,
        );
        if (getShipmentQueryId === context.state.getShipmentsCallCount) {
          context.commit('SET_NEXT_TOKEN', shipmentsResponse.nextToken);
          context.commit('SET_SHIPMENTS', shipmentsResponse.items);
        }
      } catch (error) {
        if (getShipmentQueryId === context.state.getShipmentsCallCount) {
          context.commit('SET_ERROR_MESSAGE', error.message);
        }
      } finally {
        if (getShipmentQueryId === context.state.getShipmentsCallCount) {
          context.commit('STOP_LOADING_SHIPMENTS');
        }
      }
    },
  },
  getters: {
    hasShipments(state) {
      return state.shipments.length > 0;
    },
    hasMoreShipments(state) {
      return state.nextToken !== null;
    },
    isSearching(state) {
      return state.searchTerm !== defaultSearchTerm;
    },
  },
  modules: {
    locations,
  },
};
